import React, { useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';

interface GridMotionBackgroundProps {
  className?: string;
  isDark?: boolean;
  enableSpaceStar?: boolean;
}

export const GridMotionBackground: React.FC<GridMotionBackgroundProps> = ({
  className,
  isDark = true,
  enableSpaceStar = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let animationFrameId: number;
    let width = 0;
    let height = 0;
    let cx = 0;
    let cy = 0;

    // Configuration
    const GRID_SIZE = enableSpaceStar ? 60 : 36;
    const SPACE_STAR_COUNT = 1500;
    const SPACE_SPEED = 0.5;
    const FOV = 350;

    // Pre-render Star Sprite for performance
    const starCanvas = document.createElement('canvas');
    starCanvas.width = 16;
    starCanvas.height = 16;
    const sCtx = starCanvas.getContext('2d');
    if (sCtx) {
      const half = 8;
      const grad = sCtx.createRadialGradient(half, half, 1, half, half, half);
      grad.addColorStop(0, 'rgba(255, 255, 255, 1)');
      grad.addColorStop(0.4, 'rgba(255, 255, 255, 0.8)');
      grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
      sCtx.fillStyle = grad;
      sCtx.fillRect(0, 0, 16, 16);
    }

    // State
    const spaceStars: SpaceStar[] = [];
    const lines: Line[] = [];

    // --- Classes ---

    class SpaceStar {
      x: number;
      y: number;
      z: number;
      baseSize: number;
      opacityOffset: number;
      isHighlight: boolean;

      constructor() {
        this.x = 0;
        this.y = 0;
        this.z = 0;
        this.baseSize = 0.8 + Math.random() * 0.2;
        this.opacityOffset = Math.random() * Math.PI * 2;
        this.isHighlight = Math.random() < 0.1;
        this.reset(true);
      }

      reset(randomZ = false) {
        const spread = 2500;
        this.x = (Math.random() - 0.5) * spread;
        this.y = (Math.random() - 0.5) * spread;
        this.z = randomZ ? Math.random() * 1000 : 1000;
      }

      update() {
        this.z -= SPACE_SPEED;
        if (this.z <= 1) {
          this.reset(false);
        }
      }
    }

    class Line {
      x: number;
      y: number;
      direction: 'horizontal' | 'vertical';
      polarity: -1 | 1;
      length: number;
      maxLength: number;
      progress: number;
      speed: number;
      colorStart: string;
      colorEnd: string;
      life: number;
      opacity: number;
      isFading: boolean;

      constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
        this.direction = Math.random() > 0.5 ? 'horizontal' : 'vertical';
        this.polarity = Math.random() > 0.5 ? 1 : -1;
        // Double length: 100 - 300 range (was 50 - 150)
        this.maxLength = 100 + Math.random() * 200;
        this.length = 0;
        this.progress = 0;
        this.speed = (enableSpaceStar ? 8 : 1) + Math.random() * 4;
        this.life = 0;
        this.opacity = 1;
        this.isFading = false;

        const colors = [
          ['#06b6d4', '#3b82f6'],
          ['#8b5cf6', '#ec4899'],
          ['#10b981', '#3b82f6'],
          ['#F7931A', '#E84142'],
        ];
        const choice = colors[Math.floor(Math.random() * colors.length)];
        this.colorStart = choice[0];
        this.colorEnd = choice[1];
      }

      update() {
        this.life++;
        this.progress += this.speed;

        if (this.progress > this.maxLength) {
          this.isFading = true;
        }

        if (this.isFading) {
          this.opacity -= 0.08;
          if (this.opacity <= 0) {
            this.opacity = 0;
            return false;
          }
        }

        return true;
      }

      draw(ctx: CanvasRenderingContext2D) {
        const headPos = this.progress;
        // Double trail length: 150 (was 75)
        const tailPos = Math.max(0, this.progress - 150);

        if (headPos <= 0) return;

        ctx.save();
        ctx.globalAlpha = this.opacity;

        ctx.beginPath();

        let startX = this.x;
        let startY = this.y;
        let endX = this.x;
        let endY = this.y;

        if (this.direction === 'horizontal') {
          startX = this.x + tailPos * this.polarity;
          endX = this.x + headPos * this.polarity;
        } else {
          startY = this.y + tailPos * this.polarity;
          endY = this.y + headPos * this.polarity;
        }

        const grad = ctx.createLinearGradient(startX, startY, endX, endY);
        grad.addColorStop(0, 'transparent');
        grad.addColorStop(0.5, this.colorStart);
        grad.addColorStop(1, this.colorEnd);

        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.5;
        ctx.shadowBlur = 8;
        ctx.shadowColor = this.colorEnd;

        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();

        ctx.restore();
      }
    }

    const getGridIntersection = () => {
      const cols = Math.ceil(width / GRID_SIZE);
      const rows = Math.ceil(height / GRID_SIZE);
      const col = Math.floor(Math.random() * cols);
      const row = Math.floor(Math.random() * rows);
      return { x: col * GRID_SIZE, y: row * GRID_SIZE };
    };

    const resize = () => {
      width = container.clientWidth;
      height = container.clientHeight;
      cx = width / 2;
      cy = height / 2;
      canvas.width = width;
      canvas.height = height;
    };

    window.addEventListener('resize', resize);
    resize();

    // Initialize Stars
    for (let i = 0; i < SPACE_STAR_COUNT; i++) {
      spaceStars.push(new SpaceStar());
    }

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      // --- 1. Draw Static Grid ---
      ctx.beginPath();
      // Grid Opacity set to 0.1
      const gridColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 1;

      for (let x = 0; x <= width; x += GRID_SIZE) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
      }

      for (let y = 0; y <= height; y += GRID_SIZE) {
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
      }
      ctx.stroke();

      // --- 2. Draw Space Stars ---
      const maxDist = Math.max(width, height) / 1.5;
      const time = Date.now() * 0.003;

      if (enableSpaceStar) {
        spaceStars.forEach((star) => {
          star.update();

          const scale = FOV / star.z;
          const x2d = star.x * scale + cx;
          const y2d = star.y * scale + cy;

          if (x2d < -20 || x2d > width + 20 || y2d < -20 || y2d > height + 20) return;

          const dx = x2d - cx;
          const dy = y2d - cy;
          const dist = Math.sqrt(dx * dx + dy * dy);

          // Opacity Logic
          const depthAlpha = 1 - (star.z / 1000) * 0.3;
          const distPct = Math.min(dist / maxDist, 1);
          const radialAlpha = Math.pow(distPct, 1.3);

          // Twinkle Logic
          let twinkle;
          if (star.isHighlight) {
            twinkle = 1.0 + 0.3 * Math.sin(time * 1.5 + star.opacityOffset);
          } else {
            twinkle = 0.8 + 0.2 * Math.sin(time + star.opacityOffset);
          }

          let finalAlpha = depthAlpha * radialAlpha * twinkle;

          if (star.isHighlight) {
            finalAlpha = finalAlpha * 2.0;
          } else {
            finalAlpha = finalAlpha * 1.5;
          }

          finalAlpha = Math.min(finalAlpha, 1);

          if (finalAlpha < 0.05) return;

          ctx.globalAlpha = finalAlpha;

          let size = star.baseSize * scale;
          if (star.isHighlight) {
            size *= 1.2;
          }

          if (!isDark) {
            ctx.fillStyle = `rgba(30, 30, 30, ${finalAlpha})`;
            ctx.beginPath();
            ctx.arc(x2d, y2d, size * 0.8, 0, Math.PI * 2);
            ctx.fill();
          } else {
            const drawSize = size * 4;
            ctx.drawImage(starCanvas, x2d - drawSize / 2, y2d - drawSize / 2, drawSize, drawSize);
          }
        });
      }

      ctx.globalAlpha = 1;

      // --- 3. Manage & Draw Shooting Lines ---
      // Increased frequency: max 8 lines (was 5), chance 0.02 (was 0.01)
      if (lines.length < 8 && Math.random() < 0.02) {
        const pos = getGridIntersection();
        lines.push(new Line(pos.x, pos.y));
      }

      for (let i = lines.length - 1; i >= 0; i--) {
        const line = lines[i];
        const alive = line.update();
        line.draw(ctx);
        if (!alive) {
          lines.splice(i, 1);
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDark, enableSpaceStar]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full h-full overflow-hidden transition-colors duration-500',
        !enableSpaceStar ? 'bg-transparent' : isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50',
        className,
      )}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      <div
        className={cn(
          'absolute inset-0 pointer-events-none',
          !enableSpaceStar
            ? isDark
              ? 'bg-[radial-gradient(circle_at_center,transparent_0%,#121212_120%)]'
              : 'bg-[radial-gradient(circle_at_center,transparent_0%,#fafafa_100%)]'
            : isDark
              ? 'bg-[radial-gradient(circle_at_center,transparent_0%,#0a0a0a_120%)]'
              : 'bg-[radial-gradient(circle_at_center,transparent_0%,rgba(255,255,255,0.8)_100%)]',
        )}
      />
    </div>
  );
};
